/**
 * ReferencePopover
 * ────────────────
 * The "Add reference" button for both the Student MyDesk tutor composer and
 * the Teacher Editor chat. Opens a friendly popup with two tabs:
 *
 *   ① Paste URL     — student/teacher pastes a URL; the chip is named after
 *                     the site. Added to the composer's reference-list state.
 *   ② From Library  — quick-pick from a list of already-published lessons.
 *                     Same chip shape.
 *
 * The chips are appended (as a compact "References:" footer) to the user's
 * next chat message right before send, so the LLM sees them as context.
 * Nothing about this requires backend changes.
 *
 * Props
 *   value              array of reference chips currently set
 *                      ({ id, kind: 'url'|'lesson', url, label })
 *   onChange           setter that receives the next array
 *   library            optional list of lessons to choose from (for the
 *                      Library tab). Each: { id, title, subject?, url? }.
 *   libraryKind        chip kind for library picks: 'lesson' (default,
 *                      chip.lesson_id) or 'hub_doc' (Deskey Hub, chip.doc_id)
 *   libraryLabel / libraryEmptyText / librarySearchPlaceholder
 *                      wording for that tab (teacher editor: "Deskey Hub")
 *   align              'left' | 'right' — popup horizontal anchor
 *   anchor             'bottom' | 'top'  — opens upward or downward
 *   testIdPrefix       data-testid prefix (defaults to 'ref-picker')
 *   accent             optional override colour
 *   maxWidth           number (px) — caps the panel width so it never spills
 *                      past a narrow host container (e.g. a resizable chat
 *                      side panel with `overflow: hidden`, where the panel
 *                      would otherwise get an edge clipped off)
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link as LinkIcon, X, Search, BookMarked, Globe, Check } from 'lucide-react';
// import { Plus } from 'lucide-react'; // Paste URL "Add reference" button (commented out)

export default function ReferencePopover({
  value = [],
  onChange,
  library = [],
  libraryKind = 'lesson',
  libraryLabel = 'Library',
  libraryEmptyText = 'No lessons available yet.',
  librarySearchPlaceholder = 'Filter lessons…',
  align = 'right',
  anchor = 'bottom',
  maxWidth = null,
  className = '',
  testIdPrefix = 'ref-picker',
  accent = '#0EA5E9',
  size = 'md',
}) {
  const [open, setOpen] = useState(false);
  // Paste URL is commented out (the AI can't open links) — the popup opens on
  // the Library / Deskey Hub tab. Uncomment the Paste URL blocks to restore it.
  const [tab, setTab] = useState('library');
  // const [url, setUrl] = useState('');
  const [q, setQ] = useState('');
  const popRef = useRef(null);
  // const urlRef = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Paste URL (commented out)
  // useEffect(() => {
  //   if (open && tab === 'url') {
  //     const t = setTimeout(() => urlRef.current?.focus(), 50);
  //     return () => clearTimeout(t);
  //   }
  // }, [open, tab]);

  const filteredLibrary = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return library;
    return library.filter(l =>
      (l.title || '').toLowerCase().includes(needle)
      || (l.subject || '').toLowerCase().includes(needle));
  }, [library, q]);

  // Which chip field holds the picked item's id.
  const libraryIdKey = libraryKind === 'hub_doc' ? 'doc_id' : 'lesson_id';
  const valueLessonIds = useMemo(() => new Set(
    value.filter(v => v.kind === libraryKind).map(v => v[libraryIdKey])
  ), [value, libraryKind, libraryIdKey]);

  // Paste URL (commented out)
  // const isValidUrl = (u) => {
  //   try {
  //     const x = new URL(u.trim());
  //     return x.protocol === 'http:' || x.protocol === 'https:';
  //   } catch { return false; }
  // };

  // Stable, testid-safe chip IDs — generated when the chip is added so
  // we don't smear `https://...` (with `/`, `:`, `?`) across DOM attributes.
  const nextChipId = () => `ref_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

  // Paste URL (commented out)
  // const addUrlChip = () => {
  //   if (!isValidUrl(url)) return;
  //   // The chip is named after the site (no separate label box).
  //   const safeLabel = (() => {
  //     try { return new URL(url.trim()).hostname.replace(/^www\./, ''); }
  //     catch { return 'Reference'; }
  //   })();
  //   // Dedupe by raw URL — clicking Add twice with the same URL should be a no-op.
  //   if (value.some(v => v.kind === 'url' && v.url === url.trim())) {
  //     setUrl('');
  //     return;
  //   }
  //   const next = [...value, {
  //     id: nextChipId(),
  //     kind: 'url',
  //     url: url.trim(),
  //     label: safeLabel,
  //   }];
  //   onChange?.(next.slice(-5)); // soft-cap at 5 references
  //   setUrl('');
  // };

  const toggleLessonChip = (l) => {
    const existing = value.find(v => v.kind === libraryKind && v[libraryIdKey] === l.id);
    if (existing) {
      onChange?.(value.filter(v => v.id !== existing.id));
    } else {
      const next = [...value, {
        id: nextChipId(),
        kind: libraryKind,
        url: l.url || null,
        label: l.title,
        subject: l.subject || null,
        [libraryIdKey]: l.id,
      }];
      onChange?.(next.slice(-5));
    }
  };

  // anchor="top" means the composer sits at the BOTTOM of the viewport and
  // the panel must open UPWARD (above the button). anchor="bottom" opens
  // downward. (This mapping was inverted before, pushing the panel below
  // the fold on both chat composers.)
  const panelClass = anchor === 'top'
    ? 'bottom-[calc(100%+8px)]'
    : 'top-[calc(100%+8px)]';

  const count = value.length;
  const sizeClass = size === 'sm' ? 'w-9 h-9' : 'w-10 h-10';

  return (
    <div className={`relative ${className}`} ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        data-testid={`${testIdPrefix}-toggle`}
        title="Attach a reference link or lesson"
        className={`${sizeClass} rounded-full flex items-center justify-center border-2 transition-all hover:-translate-y-0.5 active:scale-95 relative flex-shrink-0`}
        style={{
          borderColor: (open || count > 0) ? accent : 'rgba(0,0,0,0.10)',
          background: (open || count > 0) ? `${accent}14` : 'transparent',
          color: (open || count > 0) ? accent : '#475569',
        }}>
        <LinkIcon className="w-4 h-4" />
        {count > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-extrabold text-white flex items-center justify-center"
            style={{ background: accent }}
            data-testid={`${testIdPrefix}-count`}>
            {count}
          </span>
        )}
      </button>

      {open && (
        <div
          className={`absolute ${panelClass} ${align === 'right' ? 'right-0' : 'left-0'} max-w-[92vw] rounded-2xl bg-white overflow-hidden border border-slate-200`}
          data-testid={`${testIdPrefix}-panel`}
          style={{
            width: maxWidth ? Math.max(260, Math.min(360, maxWidth)) : 360,
            boxShadow: '0 20px 50px -12px rgba(15,23,42,0.22), 0 6px 18px -6px rgba(15,23,42,0.10)',
            // Ride above the global Emergent badge and any other floating UI.
            zIndex: 99999,
          }}>
          {/* Header */}
          <div className="px-3 py-2.5 flex items-center justify-between border-b border-slate-100 bg-gradient-to-b from-white to-slate-50/60">
            <div className="flex items-center gap-1.5">
              <LinkIcon className="w-3.5 h-3.5" style={{ color: accent }} />
              <span className="text-[10.5px] uppercase tracking-[0.18em] font-extrabold text-slate-700">Add reference</span>
            </div>
            <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-md flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors" aria-label="Close">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>

          {/* Tabs */}
          <div className="px-3 pt-2.5 flex items-center gap-1.5">
            {/* Paste URL (commented out)
            <TabButton
              active={tab === 'url'}
              onClick={() => setTab('url')}
              accent={accent}
              icon={<Globe className="w-3 h-3" />}
              label="Paste URL"
              testId={`${testIdPrefix}-tab-url`}
            />
            */}
            <TabButton
              active={tab === 'library'}
              onClick={() => setTab('library')}
              accent={accent}
              icon={<BookMarked className="w-3 h-3" />}
              label={`${libraryLabel}${library.length ? ` · ${library.length}` : ''}`}
              testId={`${testIdPrefix}-tab-library`}
            />
          </div>

          {/* Body */}
          {/* Paste URL (commented out) — to restore, put back
              `{tab === 'url' ? ( <this form> ) : ( <library body> )}`.
            <div className="px-3 pt-2.5 pb-3 space-y-2">
              <input
                ref={urlRef}
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') addUrlChip(); }}
                placeholder="https://en.wikipedia.org/wiki/Newton's_laws"
                data-testid={`${testIdPrefix}-url-input`}
                className="w-full h-9 px-3 rounded-lg text-[12px] bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:ring-2 transition-all placeholder:text-slate-400"
                style={{ '--tw-ring-color': `${accent}33` }}
              />
              <button
                onClick={addUrlChip}
                disabled={!isValidUrl(url)}
                data-testid={`${testIdPrefix}-url-add`}
                className="w-full h-9 rounded-lg text-[12px] font-bold text-white flex items-center justify-center gap-1.5 transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-110 active:scale-[0.98]"
                style={{ background: accent }}>
                <Plus className="w-3.5 h-3.5" /> Add reference
              </button>
              <p className="text-[10px] text-slate-400 px-0.5 leading-snug">
                The AI gets the link address only — it can't open the page. Paste the key text into your message if the AI needs it.
              </p>
            </div>
          */}
          {tab === 'library' && (
            <div className="px-3 pt-2.5 pb-3 space-y-2">
              {library.length > 5 && (
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder={librarySearchPlaceholder}
                    data-testid={`${testIdPrefix}-lib-search`}
                    className="w-full h-8 pl-7 pr-2.5 rounded-lg text-[12px] bg-slate-50 border border-slate-200 outline-none focus:bg-white focus:ring-2 transition-all placeholder:text-slate-400"
                    style={{ '--tw-ring-color': `${accent}33` }}
                  />
                </div>
              )}
              <div className="max-h-[240px] overflow-y-auto -mx-1 px-1">
                {filteredLibrary.length === 0 ? (
                  <div className="py-6 text-center text-[11px] text-slate-400" data-testid={`${testIdPrefix}-lib-empty`}>
                    {library.length === 0
                      ? libraryEmptyText
                      : 'Nothing matches that search.'}
                  </div>
                ) : (
                  <ul className="space-y-1.5">
                    {filteredLibrary.map(l => {
                      const checked = valueLessonIds.has(l.id);
                      return (
                        <li key={l.id}>
                          <button
                            onClick={() => toggleLessonChip(l)}
                            data-testid={`${testIdPrefix}-lib-row-${l.id}`}
                            className="w-full text-left flex items-center gap-2 px-2 py-2 rounded-lg border transition-all hover:bg-slate-50"
                            style={{
                              borderColor: checked ? accent : 'rgba(0,0,0,0.08)',
                              background: checked ? `${accent}10` : 'transparent',
                            }}>
                            <span className="w-7 h-7 rounded-md flex-shrink-0 flex items-center justify-center"
                              style={{ background: checked ? `${accent}28` : '#f1f5f9', color: checked ? accent : '#64748b' }}>
                              {checked ? <Check className="w-3.5 h-3.5" /> : <BookMarked className="w-3.5 h-3.5" />}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="text-[12px] font-semibold text-slate-800 truncate">{l.title}</div>
                              {l.subject && (
                                <div className="text-[10px] text-slate-500 truncate">{l.subject}</div>
                              )}
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Current chips preview (bottom of panel) */}
          {value.length > 0 && (
            <div className="border-t border-slate-100 px-3 py-2 bg-slate-50/60 flex flex-wrap gap-1">
              {value.map(v => (
                <span key={v.id}
                  data-testid={`${testIdPrefix}-chip-${v.id}`}
                  className="inline-flex items-center gap-1 text-[10px] font-bold pl-1.5 pr-1 py-0.5 rounded-full border-2"
                  style={{
                    borderColor: accent,
                    background: `${accent}14`,
                    color: accent,
                  }}>
                  {v.kind === 'url' ? <Globe className="w-2.5 h-2.5" /> : <BookMarked className="w-2.5 h-2.5" />}
                  <span className="max-w-[120px] truncate">{v.label}</span>
                  <button onClick={() => onChange?.(value.filter(x => x.id !== v.id))}
                    aria-label="Remove reference"
                    className="ml-0.5 hover:bg-black/10 rounded-full w-3 h-3 inline-flex items-center justify-center">
                    <X className="w-2 h-2" />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TabButton({ active, onClick, accent, icon, label, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="flex-1 flex items-center justify-center gap-1.5 h-7 rounded-md text-[10.5px] font-bold transition-all"
      style={{
        background: active ? accent : 'transparent',
        color: active ? 'white' : '#64748b',
        border: active ? 'none' : '1px solid rgba(0,0,0,0.08)',
      }}>
      {icon}
      {label}
    </button>
  );
}


/**
 * Helper: appends a compact "References:" block to a chat message before send.
 * Returns the original text if no references are set.
 */
export function appendReferencesToMessage(message, refs = []) {
  const list = (refs || []).filter(Boolean);
  if (!list.length) return message;
  const lines = list.map((r, i) => {
    const label = (r.label || '').trim() || (r.url ? new URL(r.url).hostname : `Reference ${i + 1}`);
    if (r.kind === 'lesson') {
      return `  ${i + 1}. (Lesson) ${label}`;
    }
    return `  ${i + 1}. ${label}${r.url ? ` — ${r.url}` : ''}`;
  });
  return `${message}\n\nReferences (provided by the user — use these as additional context):\n${lines.join('\n')}`;
}
